import { useState, useRef, ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePhotoUpload } from '../hooks/usePhotoUpload';

interface PhotoUploaderProps {
  apartmentId: string;
  onPhotoUploaded: (photoUrls: { key: string; photoUrl: string; thumbnailUrl: string; optimizedUrl: string; displayOrder: number }) => void;
  /** The display order to assign to the newly uploaded photo. */
  nextDisplayOrder?: number;
  maxSizeMB?: number;
  disabled?: boolean;
}

export const PhotoUploader = ({
  apartmentId,
  onPhotoUploaded,
  nextDisplayOrder = 0,
  maxSizeMB = 5,
  disabled = false,
}: PhotoUploaderProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadPhoto, uploading, uploadProgress } = usePhotoUpload(apartmentId);

  // Get the first (and only) upload progress since we upload one at a time
  const currentProgress = Object.values(uploadProgress)[0];

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(false);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('errors:invalidImageFile'));
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setError(t('errors:fileTooLarge', { sizeMB: fileSizeMB.toFixed(2), maxMB: maxSizeMB }));
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    await handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    try {
      setError(null);

      // Use the hook to upload
      const result = await uploadPhoto(file, nextDisplayOrder);

      // Notify parent component with all photo URLs
      onPhotoUploaded({ ...result, displayOrder: nextDisplayOrder });

      setSuccess(true);

      // Clear preview after 2 seconds
      setTimeout(() => {
        setPreview(null);
        setSuccess(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(err instanceof Error ? err.message : t('errors:failedToUploadPhoto'));
    }
  };

  const handleClearPreview = () => {
    setPreview(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusMessage = (): string | null => {
    if (!currentProgress) return null;

    switch (currentProgress.status) {
      case 'pending':
        return t('pages:uploadPreparing');
      case 'getting-url':
        return t('pages:uploadGettingUrl');
      case 'uploading':
        return t('pages:uploadingToCloud');
      case 'processing':
        return t('pages:processingThumbnails');
      case 'complete':
        return t('pages:uploadComplete');
      case 'error':
        return currentProgress.error ?? t('pages:uploadFailed');
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          error
            ? 'border-red-300 bg-red-50'
            : success
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300 hover:border-primary-500'
        }`}
      >
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded" />
            {!uploading && !success && (
              <button
                onClick={handleClearPreview}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                type="button"
              >
                <X size={16} />
              </button>
            )}
            {success && (
              <div className="absolute top-2 right-2 p-2 bg-green-500 text-white rounded-full">
                <CheckCircle size={20} />
              </div>
            )}
            {currentProgress?.status === 'error' && (
              <div className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full">
                <AlertCircle size={20} />
              </div>
            )}
          </div>
        ) : (
          <div>
            <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">{t('forms:photoUploadInstruction')}</p>
            <p className="text-sm text-gray-500">{t('forms:photoUploadHint', { maxMB: maxSizeMB })}</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading || disabled}
          className="hidden"
        />

        {!preview && !uploading && !success && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="mt-4 btn-primary flex items-center gap-2 mx-auto"
          >
            <Upload size={16} />
            {t('forms:chooseFile')}
          </button>
        )}
      </div>

      {uploading && currentProgress && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-primary-500" />
              <span className="text-gray-600">{getStatusMessage()}</span>
            </div>
            <span className="font-medium text-primary-600">{currentProgress.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${currentProgress.progress}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600 flex items-center gap-2">
            <CheckCircle size={16} />
            {t('pages:photoUploadSuccess')}
          </p>
        </div>
      )}
    </div>
  );
};
